// pages/BookHighlightsPage.tsx
// 書籍ごとの抜粋・メモ（ハイライト）一覧 + 作成・編集・削除 UI
import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import {
  fetchBookHighlights,
  createHighlight,
  updateHighlight,
  deleteHighlight,
} from '../utils/highlightsApi';
import { useAppTokens } from '../context/ThemeModeContext';
import { generateCoverUrlSmart } from '../utils/generateCoverUrl';
import { buildBibSimpleUrl } from '../config/api';
import { getBibTitle, getBibResponsibility, getBibIsbn } from '../utils/bibAdapter';
import {
  Highlight,
  HighlightBookItem,
  HighlightVisibility,
  BibSimpleResponse,
} from '../types/rlTypes';
import {
  Container,
  Typography,
  Box,
  CircularProgress,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';

const VISIBILITY_LABELS: Record<HighlightVisibility, string> = {
  private: '非公開',
  shared: '限定公開',
  public: '公開',
};

interface BookInfo {
  title?: string | null;
  author?: string | null;
  isbn13?: string | null;
  cover_url?: string | null;
}

const BookHighlightsPage: React.FC = () => {
  const { rlBookId } = useParams<{ rlBookId: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const { idToken } = useAuth();

  const rlBookIdNum = Number(rlBookId);
  const stateBook = (location.state as { book?: HighlightBookItem } | null)?.book;

  const [highlights, setHighlights] = useState<Highlight[]>([]);
  const [bookInfo, setBookInfo] = useState<BookInfo | null>(stateBook ?? null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // ダイアログ状態（作成・編集共用）
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingHighlight, setEditingHighlight] = useState<Highlight | null>(null);
  const [formText, setFormText] = useState('');
  const [formPageNumber, setFormPageNumber] = useState('');
  const [formVisibility, setFormVisibility] = useState<HighlightVisibility>('private');
  const [formError, setFormError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  // 削除確認ダイアログ
  const [deleteTarget, setDeleteTarget] = useState<Highlight | null>(null);
  const [deleting, setDeleting] = useState(false);

  // ===== Style tokens (GraphPage準拠) =====
  const TOKENS = useAppTokens();

  const glassPanelSx = {
    background: TOKENS.glass,
    border: `1px solid ${TOKENS.border}`,
    borderRadius: 2,
    boxShadow: '0 0 0 rgba(0,0,0,0)',
    backdropFilter: 'blur(6px)',
  } as const;

  const pillSx = {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 1,
    px: 1.25,
    py: 0.75,
    borderRadius: 999,
    background: TOKENS.glassStrong,
    border: `1px solid ${TOKENS.border}`,
    color: TOKENS.text,
    fontSize: 12,
    lineHeight: 1,
    userSelect: 'none',
  } as const;

  // ダイアログ内 TextField 用（ダークテーマに合わせる）
  const textFieldSx = {
    '& .MuiInputBase-input': { color: TOKENS.text },
    '& .MuiInputLabel-root': { color: TOKENS.textSub },
    '& .MuiInputLabel-root.Mui-focused': { color: TOKENS.text },
    '& .MuiOutlinedInput-notchedOutline': { borderColor: TOKENS.border },
    '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: TOKENS.borderSoft },
    '& .Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: TOKENS.accent },
    '& .MuiSelect-icon': { color: TOKENS.textSub },
  } as const;

  const loadHighlights = useCallback(async () => {
    if (!idToken || !rlBookIdNum) return;
    try {
      const res = await fetchBookHighlights(rlBookIdNum, idToken);
      const items: Highlight[] = res.data?.items ?? [];
      setHighlights(items);
    } catch (err) {
      console.error('Error fetching highlights:', err);
      setError('抜粋・メモの取得に失敗しました。');
    }
  }, [idToken, rlBookIdNum]);

  // 初回ロード
  useEffect(() => {
    const load = async () => {
      if (idToken) {
        setLoading(true);
        await loadHighlights();
        setLoading(false);
      }
    };
    load();
  }, [idToken, loadHighlights]);

  // 書誌情報（location.state に無い場合のみ /bib から取得）
  useEffect(() => {
    if (stateBook || !rlBookIdNum) return;
    const loadBookInfo = async () => {
      try {
        const res = await axios.get<BibSimpleResponse>(buildBibSimpleUrl(rlBookIdNum));
        const bib = res.data;
        setBookInfo({
          title: getBibTitle(bib),
          author: getBibResponsibility(bib),
          isbn13: getBibIsbn(bib),
        });
      } catch (err) {
        // 書誌が取れなくてもハイライト一覧は表示する
        console.error('Error fetching bib info:', err);
      }
    };
    loadBookInfo();
  }, [stateBook, rlBookIdNum]);

  const formatDate = (timestamp: number) => {
    const date = new Date(timestamp * 1000);
    return date.toLocaleString('ja-JP', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getCoverImage = (): string => {
    return generateCoverUrlSmart(rlBookIdNum, bookInfo?.title, bookInfo?.isbn13);
  };

  // ===== ダイアログ操作 =====
  const openCreateDialog = () => {
    setEditingHighlight(null);
    setFormText('');
    setFormPageNumber('');
    setFormVisibility('private');
    setFormError(null);
    setDialogOpen(true);
  };

  const openEditDialog = (highlight: Highlight) => {
    setEditingHighlight(highlight);
    setFormText(highlight.text ?? '');
    setFormPageNumber(highlight.page_number != null ? String(highlight.page_number) : '');
    // 「限定公開」は選択肢から外したため、既存データがそれだった場合は非公開扱いにする
    setFormVisibility(highlight.visibility === 'shared' ? 'private' : highlight.visibility);
    setFormError(null);
    setDialogOpen(true);
  };

  const closeDialog = () => {
    if (saving) return;
    setDialogOpen(false);
  };

  const parsePageNumber = (): number | null | undefined => {
    const trimmed = formPageNumber.trim();
    if (trimmed === '') return null;
    const n = Number(trimmed);
    if (!Number.isInteger(n) || n < 0) return undefined; // 不正値
    return n;
  };

  const handleSave = async () => {
    if (!idToken || !rlBookIdNum) return;

    const text = formText.trim();
    if (!text) {
      setFormError('テキストを入力してください。');
      return;
    }
    const pageNumber = parsePageNumber();
    if (pageNumber === undefined) {
      setFormError('ページ番号は0以上の整数で入力してください。');
      return;
    }

    try {
      setSaving(true);
      setFormError(null);
      if (editingHighlight) {
        await updateHighlight(
          editingHighlight.rl_highlight_id,
          { text, page_number: pageNumber, visibility: formVisibility },
          idToken,
        );
      } else {
        await createHighlight(
          { rlBookId: rlBookIdNum, text, pageNumber, visibility: formVisibility },
          idToken,
        );
      }
      await loadHighlights();
      setDialogOpen(false);
    } catch (err: any) {
      console.error('Error saving highlight:', err);
      const message = err?.response?.data?.error?.message;
      setFormError(message ? `保存に失敗しました: ${message}` : '保存に失敗しました。');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!idToken || !deleteTarget) return;
    try {
      setDeleting(true);
      await deleteHighlight(deleteTarget.rl_highlight_id, idToken);
      setDeleteTarget(null);
      await loadHighlights();
    } catch (err) {
      console.error('Error deleting highlight:', err);
      setError('抜粋・メモの削除に失敗しました。');
      setDeleteTarget(null);
    } finally {
      setDeleting(false);
    }
  };

  // Loading
  if (loading) {
    return (
      <Box
        sx={{
          width: '100vw',
          minHeight: '100dvh',
          background: `radial-gradient(1200px 600px at 20% 10%, ${TOKENS.radialGlow}, transparent 60%), ${TOKENS.bg}`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: TOKENS.text,
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  // Error
  if (error) {
    return (
      <Box
        sx={{
          width: '100vw',
          minHeight: '100dvh',
          background: `radial-gradient(1200px 600px at 20% 10%, ${TOKENS.radialGlow}, transparent 60%), ${TOKENS.bg}`,
          color: TOKENS.text,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          px: 2,
        }}
      >
        <Box sx={{ ...glassPanelSx, p: 2 }}>
          <Typography sx={{ color: TOKENS.error, fontSize: 13 }}>{error}</Typography>
        </Box>
      </Box>
    );
  }

  // Web で表示・編集するのはテキスト（manual）ハイライトのみ
  const manualHighlights = highlights.filter((h) => h.source_type === 'manual');

  return (
    <Box sx={{ mt: 7 }}>
      <Box
        sx={{
          width: '100%',
          minHeight: 'calc(100dvh - 56px)',
          background: `radial-gradient(1200px 600px at 20% 10%, ${TOKENS.radialGlow}, transparent 60%), ${TOKENS.bg}`,
          color: TOKENS.text,
          position: 'relative',
          overflowY: 'auto',
          WebkitOverflowScrolling: 'touch',
        }}
      >
        {/* top status pill */}
        <Box
          sx={{
            position: 'absolute',
            top: 14,
            left: 14,
            zIndex: 5,
            display: 'flex',
            gap: 1,
            alignItems: 'center',
            flexWrap: 'wrap',
          }}
        >
          <Box
            sx={{ ...pillSx, cursor: 'pointer', '&:hover': { borderColor: TOKENS.accent } }}
            onClick={() => navigate('/highlights')}
          >
            ← 抜粋・メモ
          </Box>
          <Box sx={{ ...pillSx, opacity: 0.9 }}>{manualHighlights.length} 件</Box>
        </Box>

        <Container
          maxWidth={false}
          sx={{
            pt: 8,
            pb: 4,
            px: { xs: 2, md: 3 },
          }}
        >
          <Box
            sx={{
              ...glassPanelSx,
              minHeight: { xs: 'auto', md: 'calc(100dvh - 96px)' },
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            {/* 書籍情報ヘッダー */}
            <Box
              sx={{
                px: 2,
                py: 1.5,
                borderBottom: `1px solid ${TOKENS.border}`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 1.5,
                flexWrap: 'wrap',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0 }}>
                <Box
                  component="img"
                  src={getCoverImage()}
                  alt={bookInfo?.title ?? ''}
                  onClick={() => navigate(`/book/${rlBookIdNum}`)}
                  sx={{
                    height: 64,
                    width: 46,
                    objectFit: 'contain',
                    borderRadius: 1,
                    border: `1px solid ${TOKENS.border}`,
                    background: TOKENS.cardBg,
                    cursor: 'pointer',
                    flexShrink: 0,
                  }}
                />
                <Box sx={{ minWidth: 0 }}>
                  <Typography
                    onClick={() => navigate(`/book/${rlBookIdNum}`)}
                    sx={{
                      fontSize: 14,
                      color: TOKENS.text,
                      cursor: 'pointer',
                      lineHeight: 1.3,
                      display: '-webkit-box',
                      WebkitBoxOrient: 'vertical',
                      WebkitLineClamp: 2,
                      overflow: 'hidden',
                      '&:hover': { textDecoration: 'underline' },
                    }}
                  >
                    {bookInfo?.title ?? `書籍 #${rlBookIdNum}`}
                  </Typography>
                  {bookInfo?.author && (
                    <Typography
                      sx={{
                        fontSize: 12,
                        color: TOKENS.textSub,
                        mt: 0.25,
                        display: '-webkit-box',
                        WebkitBoxOrient: 'vertical',
                        WebkitLineClamp: 1,
                        overflow: 'hidden',
                      }}
                    >
                      {bookInfo.author}
                    </Typography>
                  )}
                </Box>
              </Box>

              <Button
                startIcon={<AddIcon />}
                onClick={openCreateDialog}
                sx={{
                  ...pillSx,
                  textTransform: 'none',
                  '&:hover': { borderColor: TOKENS.accent, background: TOKENS.glassStrong },
                }}
              >
                抜粋・メモを追加
              </Button>
            </Box>

            {/* body: ハイライト一覧 */}
            <Box sx={{ px: 2, py: 2, flex: 1, minHeight: 0 }}>
              {manualHighlights.length === 0 ? (
                <Typography sx={{ fontSize: 12.5, color: TOKENS.textSub }}>
                  この本の抜粋・メモはまだありません。「抜粋・メモを追加」から作成できます。
                </Typography>
              ) : (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                  {manualHighlights.map((highlight) => (
                    <Box
                      key={highlight.rl_highlight_id}
                      sx={{
                        background: TOKENS.cardBg,
                        border: `1px solid ${TOKENS.border}`,
                        borderRadius: 2,
                        p: 1.75,
                        transition: '0.25s',
                        '&:hover': { border: `1px solid ${TOKENS.accent}` },
                      }}
                    >
                      <Typography
                        sx={{
                          fontSize: 13.5,
                          color: TOKENS.text,
                          lineHeight: 1.7,
                          whiteSpace: 'pre-wrap',
                          wordBreak: 'break-word',
                        }}
                      >
                        {highlight.text}
                      </Typography>

                      <Box
                        sx={{
                          mt: 1.25,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          gap: 1,
                          flexWrap: 'wrap',
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                          {highlight.page_number != null && (
                            <Box sx={{ ...pillSx, px: 1, py: 0.5, fontSize: 11 }}>
                              p.{highlight.page_number}
                            </Box>
                          )}
                          <Box sx={{ ...pillSx, px: 1, py: 0.5, fontSize: 11, opacity: 0.85 }}>
                            {VISIBILITY_LABELS[highlight.visibility] ?? highlight.visibility}
                          </Box>
                          <Typography sx={{ fontSize: 11, color: TOKENS.textSub }}>
                            {formatDate(highlight.created_at)}
                          </Typography>
                        </Box>

                        <Box sx={{ display: 'flex', gap: 0.5 }}>
                          <IconButton
                            size="small"
                            aria-label="編集"
                            onClick={() => openEditDialog(highlight)}
                            sx={{ color: TOKENS.textSub, '&:hover': { color: TOKENS.text } }}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                          <IconButton
                            size="small"
                            aria-label="削除"
                            onClick={() => setDeleteTarget(highlight)}
                            sx={{ color: TOKENS.textSub, '&:hover': { color: TOKENS.error } }}
                          >
                            <DeleteOutlineIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      </Box>
                    </Box>
                  ))}
                </Box>
              )}
            </Box>
          </Box>
        </Container>
      </Box>

      {/* 作成・編集ダイアログ */}
      <Dialog
        open={dialogOpen}
        onClose={closeDialog}
        fullWidth
        maxWidth="sm"
        PaperProps={{
          sx: {
            background: TOKENS.glassStrong,
            border: `1px solid ${TOKENS.border}`,
            borderRadius: 2,
            backdropFilter: 'blur(6px)',
            color: TOKENS.text,
            backgroundImage: 'none',
          },
        }}
      >
        <DialogTitle sx={{ fontSize: 15, color: TOKENS.text }}>
          {editingHighlight ? '抜粋・メモを編集' : '抜粋・メモを追加'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            <TextField
              label="テキスト"
              value={formText}
              onChange={(e) => setFormText(e.target.value)}
              multiline
              minRows={4}
              maxRows={12}
              fullWidth
              required
              placeholder="本からの抜粋やメモを入力"
              sx={textFieldSx}
            />
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              <TextField
                label="ページ番号（任意）"
                value={formPageNumber}
                onChange={(e) => setFormPageNumber(e.target.value)}
                type="number"
                inputProps={{ min: 0, step: 1 }}
                sx={{ ...textFieldSx, width: 180 }}
              />
              <TextField
                label="公開範囲"
                value={formVisibility}
                onChange={(e) => setFormVisibility(e.target.value as HighlightVisibility)}
                select
                sx={{ ...textFieldSx, width: 180 }}
              >
                {(Object.keys(VISIBILITY_LABELS) as HighlightVisibility[])
                  .filter((v) => v !== 'shared')
                  .map((v) => (
                    <MenuItem key={v} value={v}>
                      {VISIBILITY_LABELS[v]}
                    </MenuItem>
                  ))}
              </TextField>
            </Box>
            {formError && (
              <Typography sx={{ color: TOKENS.error, fontSize: 12.5 }}>{formError}</Typography>
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={closeDialog} disabled={saving} sx={{ color: TOKENS.textSub }}>
            キャンセル
          </Button>
          <Button
            onClick={handleSave}
            disabled={saving}
            variant="outlined"
            sx={{
              color: TOKENS.text,
              borderColor: TOKENS.borderSoft,
              '&:hover': { borderColor: TOKENS.accent },
            }}
          >
            {saving ? '保存中…' : '保存'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* 削除確認ダイアログ */}
      <Dialog
        open={Boolean(deleteTarget)}
        onClose={() => !deleting && setDeleteTarget(null)}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            background: TOKENS.glassStrong,
            border: `1px solid ${TOKENS.border}`,
            borderRadius: 2,
            backdropFilter: 'blur(6px)',
            color: TOKENS.text,
            backgroundImage: 'none',
          },
        }}
      >
        <DialogTitle sx={{ fontSize: 15, color: TOKENS.text }}>抜粋・メモを削除</DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: 13, color: TOKENS.textSub }}>
            この抜粋・メモを削除します。よろしいですか？
          </Typography>
          {deleteTarget?.text && (
            <Typography
              sx={{
                mt: 1.5,
                fontSize: 12.5,
                color: TOKENS.text,
                background: TOKENS.cardBgHover,
                border: `1px solid ${TOKENS.border}`,
                borderRadius: 1.5,
                p: 1.25,
                display: '-webkit-box',
                WebkitBoxOrient: 'vertical',
                WebkitLineClamp: 3,
                overflow: 'hidden',
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
              }}
            >
              {deleteTarget.text}
            </Typography>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => setDeleteTarget(null)}
            disabled={deleting}
            sx={{ color: TOKENS.textSub }}
          >
            キャンセル
          </Button>
          <Button
            onClick={handleDelete}
            disabled={deleting}
            variant="outlined"
            sx={{
              color: TOKENS.error,
              borderColor: TOKENS.borderSoft,
              '&:hover': { borderColor: TOKENS.error },
            }}
          >
            {deleting ? '削除中…' : '削除する'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default BookHighlightsPage;
